import { useState } from 'react'
import { AmountInput, Button, DatePicker } from '../../components'
import { useCompletePlanned } from '../../lib/api/planning'
import type { PlannedTransaction } from '../../lib/api/planning'
import { fmtMoney } from '../../lib/money'

type Props = {
  planned: PlannedTransaction
  onClose: () => void
}

type Mode = 'new' | 'link'

export function CompleteModal({ planned, onClose }: Props) {
  const [mode, setMode] = useState<Mode>('new')
  const [actualDate, setActualDate] = useState(planned.plannedDate)
  const [actualAmount, setActualAmount] = useState(String(planned.amount))
  const [transactionId, setTransactionId] = useState('')
  const [error, setError] = useState<string | null>(null)

  const complete = useCompletePlanned()

  const submit = async () => {
    setError(null)
    try {
      if (mode === 'link') {
        if (!transactionId.trim()) {
          setError('Вкажіть ID транзакції')
          return
        }
        await complete.mutateAsync({ id: planned.id, transactionId: transactionId.trim() })
      } else {
        await complete.mutateAsync({
          id: planned.id,
          actualDate,
          actualAmount: Number(actualAmount),
        })
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Невідома помилка')
    }
  }

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 200,
          display: 'grid',
          placeItems: 'center',
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 201,
          background: 'var(--bg-1)',
          border: '1px solid var(--border-default)',
          borderRadius: 8,
          width: 440,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Виконати планову операцію</h2>
          <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>
            {planned.plannedDate} · {fmtMoney(planned.amount, planned.currency)}
            {planned.description ? ` · ${planned.description}` : ''}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <ModeBtn active={mode === 'new'} onClick={() => setMode('new')}>
            Створити нову транзакцію
          </ModeBtn>
          <ModeBtn active={mode === 'link'} onClick={() => setMode('link')}>
            Привʼязати існуючу
          </ModeBtn>
        </div>

        {mode === 'new' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <FieldRow label="Фактична дата">
              <DatePicker className="input" value={actualDate} onChange={setActualDate} />
            </FieldRow>
            <FieldRow label="Фактична сума">
              <AmountInput
                value={actualAmount}
                onChange={setActualAmount}
                decimals={2}
                className="input num"
              />
            </FieldRow>
          </div>
        )}

        {mode === 'link' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <FieldRow label="ID транзакції">
              <input
                type="text"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="uuid транзакції"
                className="input"
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
              />
            </FieldRow>
          </div>
        )}

        {error && <div style={{ fontSize: 13, color: 'var(--danger-fg)' }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Скасувати
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={complete.isPending}
          >
            {complete.isPending ? 'Виконуємо…' : 'Виконати'}
          </Button>
        </div>
      </div>
    </>
  )
}

function ModeBtn({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: '6px 10px',
        fontSize: 13,
        border: '1px solid',
        borderColor: active ? 'var(--accent)' : 'var(--border-default)',
        borderRadius: 6,
        background: active ? 'var(--accent-bg)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--fg-2)',
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {children}
    </button>
  )
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 11,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  )
}
